import { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import { defaultTo, sort } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';

import useControlNext from '@gef-ui/form-core/hooks/state/useControlNext';
import useFormFieldValue from '@gef-ui/form-core/hooks/state/useFormFieldValue';
import { resetForm } from '@gef-ui/form-core/stream/transfer/control';
import SubmitButton from '@gef-ui/form-material/SubmitButton';
import TextField from '@gef-ui/form-material/TextField';
import { getLabelMessage } from '@gef-ui/localization/messages';

import { ComponentLoader } from 'components';
import SectionHeader from 'components/atoms/GefMui/SectionHeader';
import SingleNote from 'components/atoms/GefMui/SingleNote';
import { useIgnoreErrorsWhen } from 'components/molecules/ComponentLoader/hooks';
import { MaxLengths } from 'constants/forms';
import { EMPTY_VALUES } from 'constants/general';
import fetch from 'features/common/fetch';
import { postNote } from 'features/views/taskDetail/actions';
import { TASK_STATUSES } from 'features/views/taskDetail/constants';
import { FIELDS, TASK_DETAIL_FORM } from 'features/views/taskDetail/form';
import { getStatusId, getTaskComments } from 'features/views/taskDetail/selectors';

const { urls } = fetch.constants;

const TaskDetailNotes = ({ taskId, onReload }) => {
	const dispatch = useDispatch();
	const next = useControlNext(TASK_DETAIL_FORM);

	const [noteFieldOpened, setNoteFieldOpened] = useState(false);

	const statusId = useSelector(getStatusId);
	const taskComments = useSelector(getTaskComments);
	// oldest to newest
	const sortedNotes = sort((a, b) => a?.dateCreated.localeCompare(b?.dateCreated))(
		defaultTo(EMPTY_VALUES.ARRAY)(taskComments),
	);
	const noteValue = useFormFieldValue({ fieldName: FIELDS.note });

	const hasNotes = !isNilOrEmpty(sortedNotes);
	const taskIsNotExpired = statusId !== TASK_STATUSES.EXPIRED;

	const handleOpenNoteField = useCallback(() => {
		setNoteFieldOpened(true);
	}, []);

	const handleCancelNoteField = useCallback(() => {
		setNoteFieldOpened(false);
		next(resetForm());
	}, [next]);

	const handleSaveNote = useCallback(async () => {
		const noteAdded = await dispatch(postNote(taskId));
		if (noteAdded) {
			setNoteFieldOpened(false);
		}
	}, [dispatch, taskId]);

	const ignoreErrorsForTaskDetailUserHasNoAccess = useIgnoreErrorsWhen({
		fetchStorePath: urls.taskDetail,
		attributePath: ['metaData', 'parsedContent', 'status'],
		value: 403,
	});

	return (
		<ComponentLoader
			fetchStorePath={urls.taskDetail}
			handleReload={onReload}
			ignoreErrors={ignoreErrorsForTaskDetailUserHasNoAccess}
		>
			<ComponentLoader fetchStorePath={urls.taskNote}>
				<Stack spacing={1} sx={{ mb: 2 }}>
					<SectionHeader text={getLabelMessage('detail:notesSubheader')} />
					<Alert sx={{ mb: 1 }} severity="info">
						{getLabelMessage('detail:notesInfo')}
					</Alert>
				</Stack>
				{taskIsNotExpired && !noteFieldOpened && (
					<Box sx={{ mt: 1, mb: 4 }}>
						<Button variant="contained" onClick={handleOpenNoteField} fullWidth>
							{getLabelMessage('singleNote:addNote')}
						</Button>
					</Box>
				)}
				{taskIsNotExpired && noteFieldOpened ? (
					<Stack spacing={5}>
						<Grid container>
							<Grid size={12}>
								<Grid container spacing={2}>
									<Grid size={12}>
										<TextField
											size="small"
											fieldName={FIELDS.note}
											label={getLabelMessage('detail:noteFieldLabel')}
											fullWidth
											multiline
											rows={4}
											slotProps={{
												htmlInput: {
													maxLength: MaxLengths.NOTE_BIG,
												},
											}}
										/>
									</Grid>
									<Grid size={12}>
										<Grid
											container
											spacing={2}
											sx={{
												justifyContent: 'flex-end',
											}}
										>
											<Grid size={3}>
												<Button variant="outlined" fullWidth onClick={handleCancelNoteField}>
													{getLabelMessage('common:cancel')}
												</Button>
											</Grid>
											<Grid size={3}>
												<SubmitButton
													variant="contained"
													fullWidth
													onClick={handleSaveNote}
													disabled={isNilOrEmpty(noteValue)}
													text={getLabelMessage('common:save')}
												/>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
						{hasNotes && <Divider />}
						<Stack spacing={5}>
							{hasNotes &&
								sortedNotes.map((item) => (
									<SingleNote
										noteContentLabel={getLabelMessage('detail:noteFieldLabel')}
										supportHtml
										key={item?.id}
										id={item?.id}
										item={item}
									/>
								))}
						</Stack>
					</Stack>
				) : (
					<Stack spacing={5}>
						{hasNotes &&
							sortedNotes.map((item) => (
								<SingleNote
									noteContentLabel={getLabelMessage('detail:noteFieldLabel')}
									supportHtml
									key={item?.id}
									id={item?.id}
									item={item}
								/>
							))}
					</Stack>
				)}
			</ComponentLoader>
		</ComponentLoader>
	);
};

TaskDetailNotes.propTypes = {
	onReload: PropTypes.func,
	statusId: PropTypes.number,
	taskId: PropTypes.string,
};

export default TaskDetailNotes;
